/**
 * Constants and configuration for NewCustomerSignupModal.
 *
 * NOTE: GraphQL queries/mutations have been moved to @/auth/store/queries.ts
 * to follow codebase patterns and enable type-safe code generation.
 */
import { BillingInterval } from '@/__generated__/graphql';
import { MIN_HOURS_NOTICE_WITHOUT_ER } from '@/consultation';
import { policies } from '@/core/agreements';

export const PRIVACY_POLICY = policies.privacy;
export const TOU_POLICY = policies['terms-of-use'];

// V2: Stepper only shows on Your Details and Payment steps (not on Schedule Call)
export const STEPS = ['Your Details', 'Payment'];

// SMB Signup configuration
export const SMB_SIGNUP_CONFIG = {
  SEAT_COUNT: 1,
  BILLING_INTERVAL: BillingInterval.Month,
} as const;

// Flow configuration
export const FLOW_CONFIG = {
  // Default consultation duration in minutes
  DEFAULT_DURATION_MINUTES: 60,
  // Time offset for suggested times (hours from now)
  SUGGESTED_TIME_HOUR_OFFSET: MIN_HOURS_NOTICE_WITHOUT_ER,
  // Interval between suggested times (hours)
  SUGGESTED_TIME_INTERVAL: 1,
  // Modal width (matches RequestConsultation dialog)
  MODAL_WIDTH: 640,
} as const;

// Step indices for navigation
export const STEP_INDICES = {
  SCHEDULING: 0,
  USER_DETAILS: 1,
  PAYMENT: 2,
} as const;

// Duration options for consultation scheduling
export const DURATION_OPTIONS = {
  '30m': { label: '30 minutes', minutes: 30 },
  '1h': { label: '1 hour', minutes: 60 },
  '1.5h': { label: '1.5 hours', minutes: 90 },
  '2h': { label: '2 hours', minutes: 120 },
} as const;

// Pricing multiplier for SMB consultations (25% service fee)
export const SMB_PRICING_MULTIPLIER = 1.25;

// Shown wherever an unpriceable expert blocks submission, and thrown by the PaymentStep
// handler guards, so the message a blocked user reads is the one a raced submit surfaces
// (ENG-5571).
export const RATE_UNAVAILABLE_SUBMIT_MESSAGE =
  "We can't show a price for this expert right now, so this booking can't be submitted. Please contact support.";

// OTP Configuration
export const OTP_CONFIG = {
  CODE_LENGTH: 6,
  EXPIRY_SECONDS: 300, // 5 minutes
  RESEND_COOLDOWN_SECONDS: 30,
  MAX_ATTEMPTS: 5,
} as const;

// OTP Stepper configurations for different user flows
export const OTP_STEPS = {
  // 3 steps for new users
  NEW_USER: ['Verify Phone', 'Your Details', 'Payment'],
  // 2 steps for returning/pending users (both OTP and email/password authenticated)
  RETURNING_USER: ['Authenticate', 'Submit Request'],
} as const;
